import { useCallback, useState } from 'react'
import { Platform, Share } from 'react-native'
import { api } from '@api'
import { useLanguage } from '@context'
import { showInfoToast, showSuccessToast } from '@utils'
import { useApiErrorHandler } from './useApiErrorHandler'

const EXPORT_FILENAME = 'keeptouch-contacts.vcf'

/**
 * Premium contacts export. Fetches the vCard from `GET /me/contacts/export`,
 * then delivers it per platform:
 *   - web    → triggers a `.vcf` file download in the browser
 *   - native → opens the OS share sheet with the vCard content (no extra native
 *              dependency, so it ships via OTA)
 *
 * A free account hits HTTP 402 (`PLAN_FEATURE_EXPORT_DISABLED`); the shared
 * error handler routes that to the Premium paywall instead of an error toast.
 */
export const useExportContacts = () => {
  const { t } = useLanguage()
  const handleError = useApiErrorHandler()
  const [isExporting, setIsExporting] = useState(false)

  const exportContacts = useCallback(async () => {
    setIsExporting(true)
    try {
      const { data } = await api.get<string>('/me/contacts/export', {
        responseType: 'text',
        // The body is a vCard, not JSON — keep the raw string so axios does not
        // try to parse it.
        transformResponse: (raw) => raw,
      })
      const vcard = typeof data === 'string' ? data : String(data)

      // Nobody to export: say so, instead of an empty file under « Export réussi ».
      if (!vcard.includes('BEGIN:VCARD')) {
        showInfoToast(t('export.emptyTitle'), t('export.emptyMessage'))
        return
      }

      if (Platform.OS === 'web') {
        const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = EXPORT_FILENAME
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        URL.revokeObjectURL(url)
      } else {
        await Share.share({ message: vcard, title: EXPORT_FILENAME })
      }

      showSuccessToast(t('export.successTitle'), t('export.successMessage'))
    } catch (error) {
      handleError(error)
    } finally {
      setIsExporting(false)
    }
  }, [t, handleError])

  return { exportContacts, isExporting }
}
